import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react'
import { useQuoteById, type Quote_ } from 'src/api/db/apiQuote'
import { useQuoteItemApi, type QuoteItem_ } from 'src/api/db/apiQuoteItem'
import type { ShippingLocation_ } from 'src/api/db/apiShippingLocations'
import type { ShipmentWithLocation } from 'src/api/db/apiShipment'
import { useEnsureQuoteItemsOnCanvas } from './useEnsureQuoteItemsOnCanvas'

export type DemoAdvanceAction =
  | 'approve_quote'
  | 'approve_proofs'
  | 'place_order'

const isQuoteNotFoundError = (error: unknown): boolean =>
  !!error &&
  typeof error === 'object' &&
  'code' in error &&
  (error as { code?: string }).code === 'PGRST116'

export type RequestContextValue = {
  publicId?: string
  quote: Quote_ | undefined
  quoteItems: QuoteItem_[] | undefined
  isError: boolean
  /** Quote query settled with no row (missing, deleted, or no access). */
  isQuoteMissing: boolean
  isQuotePending: boolean
  isQuoteItemsPending: boolean
  refetchQuote: () => void
  /** Home-page walkthrough: skip fetches/mutations and play dummy data. */
  isDemo?: boolean
  demoShipment?: ShipmentWithLocation
  demoShippingLocations?: ShippingLocation_[]
  onDemoAdvance?: (action: DemoAdvanceAction) => void
}

const RequestContext = createContext<RequestContextValue | null>(null)

export const RequestProvider = ({
  publicId,
  value: injectedValue,
  readOnly = false,
  priceInQuoteCurrency = false,
  children,
}: {
  publicId?: string
  value?: RequestContextValue
  /** Skip canvas stub writes (public share and other view-only surfaces). */
  readOnly?: boolean
  /**
   * Show prices in the quote's own currency (its organization's, default SEK)
   * instead of the viewer's. Used by the public proposal page.
   */
  priceInQuoteCurrency?: boolean
  children: ReactNode
}) => {
  if (injectedValue) {
    return (
      <RequestContext.Provider value={injectedValue}>
        {children}
      </RequestContext.Provider>
    )
  }

  return (
    <RequestProviderFromQuote
      publicId={publicId}
      readOnly={readOnly}
      priceInQuoteCurrency={priceInQuoteCurrency}
    >
      {children}
    </RequestProviderFromQuote>
  )
}

const RequestProviderFromQuote = ({
  publicId,
  readOnly,
  priceInQuoteCurrency,
  children,
}: {
  publicId?: string
  readOnly: boolean
  priceInQuoteCurrency: boolean
  children: ReactNode
}) => {
  const quoteQuery = useQuoteById(publicId)
  const quoteItemsQuery = useQuoteItemApi({
    quote_id: quoteQuery.data?.id || undefined,
    displayCurrency: priceInQuoteCurrency
      ? (quoteQuery.data?.organization?.currency ?? 'SEK')
      : undefined,
  })

  useEnsureQuoteItemsOnCanvas(
    readOnly ? undefined : quoteQuery.data?.id,
    readOnly ? undefined : quoteItemsQuery.quoteItems.data
  )

  const value = useMemo<RequestContextValue>(
    () => ({
      publicId,
      quote: quoteQuery.data ?? undefined,
      quoteItems: quoteItemsQuery.quoteItems.data,
      isError: quoteQuery.isError || quoteItemsQuery.quoteItems.isError,
      isQuoteMissing:
        !quoteQuery.isPending &&
        !quoteQuery.data &&
        (isQuoteNotFoundError(quoteQuery.error) || !quoteQuery.isError),
      isQuotePending: quoteQuery.isPending,
      isQuoteItemsPending: quoteItemsQuery.quoteItems.isPending,
      refetchQuote: () => {
        void quoteQuery.refetch()
      },
    }),
    [
      publicId,
      quoteQuery.data,
      quoteQuery.isPending,
      quoteQuery.isError,
      quoteQuery.error,
      quoteQuery.refetch,
      quoteItemsQuery.quoteItems.data,
      quoteItemsQuery.quoteItems.isPending,
      quoteItemsQuery.quoteItems.isError,
    ]
  )

  return (
    <RequestContext.Provider value={value}>{children}</RequestContext.Provider>
  )
}

export const useRequestContext = () => {
  const context = useContext(RequestContext)
  if (!context) {
    throw new Error('useRequestContext must be used within RequestProvider')
  }
  return context
}

/** Share-order and other surfaces that may render without RequestProvider. */
export const useOptionalRequestContext = () => useContext(RequestContext)
